import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, select, put, take, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import { IPlayerState, IVideoState, updatePlayerState, updateVideoState, VideoPlayState } from 'core/slices/player';
import { EntityTypes } from 'constants/entities';
import { ActionBehaviour, IActionBasic } from 'types/domain/action';
import { selectAction, selectActionVoiceSceneId } from 'core/selectors/product/action/action';
import { IUpdateState, updateState } from 'core/actions/product/state';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { selectSceneConfig, selectVoiceSceneConfig } from 'core/selectors/product/scene/sceneConfig';
import { ISceneConfig, ISceneDAO, SceneType } from 'types/domain/scene';
import { AssetType, IAsset } from 'types/app/asset';
import { loadAsset } from 'core/services/asset';
import { LoadingState } from 'types/domain/base';
import { dashPlayerEvents, VideoEvents, videoPlaybackEvents } from 'utils/videoEvents';
import { isIOS, isSafari } from 'react-device-detect';
import { selectScene } from 'core/selectors/product/scene/scene';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { loaders } from '../product/assets/load';
import { dashjs } from 'utils/loadDash';
import { unloadAsset } from 'core/slices/assets';

import { prev } from 'core/slices/register';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';

let DashPlayer = null;

function* assetLoaded(action) {
  const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (
    sceneAsset &&
    action.meta.arg.id === sceneAsset.id &&
    sceneAsset.type === AssetType.VIDEO &&
    videoState.assetId !== action.meta.arg.id
  )
    yield loadSceneVideo(sceneAsset, 'assetLoaded');
}

function* loadSceneVideo(asset: IAsset, caller?: string) {
  // console.log('loadSceneVideo', asset, caller);

  if (asset.type !== AssetType.VIDEO) return console.warn('trying to load an asset that isnt video');

  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  if (asset.id !== videoState.assetId) yield removeVideo('initial load remove');

  yield put(yield call(updateVideoState, { assetId: asset.id, ready: false }));
  const config: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const configuredUrl = config?.start ? `${asset.url}#t=${config.start}` : asset.url;

  if (isIOS || isSafari) {
    videoEl.src = configuredUrl;
    videoEl.load();
  } else yield fork(loadDash, asset, configuredUrl);

  //mute while managing
  videoEl.muted = true;

  yield manageVideo();
}

function* loadDash(asset: IAsset, url) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  while (asset.loading !== LoadingState.LOADED) {
    yield take(loadAsset.fulfilled);
    // eslint-disable-next-line no-loop-func
    asset = yield select((state: RootState) => state.core.assets[asset.id]);
  }

  if (loaders[asset.id]) {
    if (!loaders[asset.id].attachView) return; //console.warn('no attachView for', asset.id);

    yield call(loaders[asset.id].attachView, videoEl);
    const mode: Mode = yield select(selectSessionMode);
    if (loaders[asset.id].isReady() && mode === Mode.ADMIN) yield startVideo();
    return;
  }

  if (DashPlayer == null) {
    const dash = yield call(dashjs);
    DashPlayer = yield call(dash.MediaPlayer().create);
    yield fork(dashEvents, DashPlayer, dash.MediaPlayer);
    if (videoEl) yield call([DashPlayer, DashPlayer.initialize], videoEl, null, true);
  }

  try {
    yield call([DashPlayer, DashPlayer.attachSource], url);
  } catch (e) {
    console.log(e);
  }
}
function* dashEvents(DashPlayer, MediaPlayer) {
  const videoEvents = yield call(dashPlayerEvents, DashPlayer, MediaPlayer);
  const assetId = yield select((state: RootState) => state.core.player.state.video.assetId);
  let aId = assetId;

  while (aId === assetId) {
    let event: Event = yield take(videoEvents);

    aId = yield select((state: RootState) => state.core.player.state.video.assetId);

    if (event.type === 'error') {
      yield put(yield call(updateVideoState, { play: VideoPlayState.ERROR }));
      yield put(yield call(updatePlayerState, { paused: true, culprit: 'dashError' }));
    }

    if (event.type === 'streamTeardownComplete') {
      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
      if (sceneAsset.fallback && videoState.play === VideoPlayState.ERROR) {
        const config: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
        const configuredUrl = config?.start ? `${sceneAsset.fallback}#t=${config.start}` : sceneAsset.fallback;
        yield call([DashPlayer, DashPlayer.attachSource], configuredUrl);
      }
    }
  }
  // Critical: Close the event channel to remove all event listeners
  videoEvents.close();
}

export function* removeVideo(cause = null) {
  //console.warn('removeVideo', cause);

  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (videoState.assetId) yield put(yield call(unloadAsset, videoState.assetId));

  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;

  if (DashPlayer && DashPlayer.isReady()) {
    DashPlayer.pause();
    DashPlayer.initialize();
  } else if ((isIOS || isSafari) && videoEl.src) {
    videoEl.src = '';
    videoEl.load();
  }
  yield put(yield call(updateVideoState, { assetId: null, play: VideoPlayState.PAUSED, ready: false, ended: false }));
}

function* updatePlayerStateSaga(action: PayloadAction<Partial<IPlayerState>>) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoState: VideoPlayState = yield select((state: RootState) => state.core.player.state.video.play);
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  //TODO - more logic if it's a voice over a video scene
  const config = voiceSceneConfig ?? sceneConfig;

  const actionVoiceSceneId = yield select(state => selectActionVoiceSceneId(state, undefined));

  if (action.payload.paused === undefined || !config || (!scene && !actionVoiceSceneId)) return;
  // console.warn('cannot play 1', {
  //   paused: action.payload.paused,
  //   noConfig: !config,
  //   noScene: !scene && !actionVoiceSceneId,
  // });

  if (!action.payload.paused) {
    if (
      (!actionVoiceSceneId && scene.type !== SceneType.VIDEO) ||
      !videoEl.src ||
      !videoEl.src.length ||
      Math.abs(videoEl.currentTime - (config.end || videoEl.duration)) < 0.1
    )
      return;
    // console.warn('cannot play 2', {
    //   invalidSceneType: !actionVoiceSceneId && scene.type !== SceneType.VIDEO,
    //   noSrc: !videoEl.src,
    //   noSrcLength: !videoEl.src.length,
    //   nearEnd: Math.abs(videoEl.currentTime - (config.end || videoEl.duration)) < 0.1,
    // });
  }

  try {
    if (
      ![VideoPlayState.PAUSED, VideoPlayState.START_PAUSING].includes(videoState) &&
      action.payload.paused &&
      !videoEl.paused
    ) {
      yield put(yield call(updateVideoState, { play: VideoPlayState.START_PAUSING }));
      yield videoEl.pause();
    }
    if (
      ![VideoPlayState.PLAYING, VideoPlayState.START_PLAYING].includes(videoState) &&
      action.payload.paused === false &&
      videoEl.paused
    ) {
      yield put(yield call(updateVideoState, { play: VideoPlayState.START_PLAYING }));
      yield videoEl.play();
    }
  } catch (e) {
    console.log(e);
  }
}

function* manageVideo() {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoEvents = yield call(videoPlaybackEvents);
  const assetId = yield select((state: RootState) => state.core.player.state.video.assetId);
  let aId = assetId;

  while (assetId === aId) {
    let event: Event = yield take(videoEvents);

    const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
    aId = videoState.assetId;

    switch (event.type) {
      case VideoEvents.PLAYING:
        if (![VideoPlayState.PLAYING].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.PLAYING, buffering: false, seeking: false }));
        break;
      case VideoEvents.PAUSE:
        if (![VideoPlayState.PAUSED].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.PAUSED }));
        break;
      case VideoEvents.WAITING:
        if (![VideoPlayState.BUFFERING].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.BUFFERING, buffering: true }));
        break;
      case VideoEvents.EMPTIED:
        if (![VideoPlayState.SEEKING, VideoPlayState.BUFFERING].includes(videoState.play) && videoState.ready)
          if (videoState.assetId)
            yield put(
              yield call(updateVideoState, {
                play: VideoPlayState.EMPTIED,
                assetId: null,
                buffering: false,
                seeking: false,
              }),
            );
        break;
    }

    const { ready } = yield select((state: RootState) => state.core.player.state.video);

    if (!ready && videoEl.readyState === 4) {
      if (
        [VideoEvents.SEEKED, VideoEvents.PROGRESS, VideoEvents.CANPLAY, VideoEvents.CANPLAYTHROUGH].includes(
          event.type as VideoEvents,
        )
      )
        yield startVideo();
    }
    const ended = yield select((state: RootState) => state.core.player.state.video.ended);

    if (!ended) {
      if ([VideoEvents.ENDED].includes(event.type as VideoEvents)) yield endVideo();

      const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
      const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
      //TODO - more logic if it's a voice over a video scene
      const action: IActionBasic = yield select(state => selectAction(state, undefined));
      const config = action?.voiceSceneConfigId ? voiceSceneConfig : sceneConfig;

      const end = config?.end || videoEl.duration;

      if (videoEl.currentTime >= end) yield endVideo();
    }
  }
  videoEvents.close();
}

function* startVideo(newVideo = true) {
  const { ready } = yield select((state: RootState) => state.core.player.state.video);
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const action: IActionBasic = yield select(state => selectAction(state, undefined));
  //TODO - more logic if it's a voice over a video scene
  const config = action.voiceSceneConfigId ? voiceSceneConfig : sceneConfig;

  if (!config) return console.warn('cant play video - no config');

  const start = config.start || 0.1;

  if (!videoEl) return console.warn('cant play video - no video');

  if (videoState.ended) yield put(yield call(updateVideoState, { ended: false }));

  if (Math.abs(videoEl.currentTime - start) < 0.1) {
    if (newVideo && !ready) {
      yield put(yield call(updatePlayerState, { paused: true, cuprit: 1 }));
      yield put(yield call(updateVideoState, { ready: true }));
    }
  } else {
    if (ready) yield put(yield call(updateVideoState, { ready: false }));

    yield put(yield call(updateVideoState, { play: VideoPlayState.SEEKING, seeking: true }));

    videoEl.currentTime = start;
    return;
  }

  if (videoEl.volume !== config.volume || videoEl.muted) {
    //if (videoState.interacted)
    videoEl.muted = config.volume === 0;
    videoEl.volume = config.volume || 1;
  } else videoEl.muted = false;
}

function* endVideo() {
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  //TODO - more logic if it's a voice over a video scene
  const config = voiceSceneConfig ?? sceneConfig;
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  if (config && config.loop) {
    const start = config.start || 0;
    videoEl.currentTime = start;
    return;
  }
  if (action && action.behaviour === ActionBehaviour.PAUSE && !action.voiceSceneId) return;

  const ended = yield select((state: RootState) => state.core.player.state.video.ended);
  if (!ended) yield put(yield call(updateVideoState, { ended: true }));
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  // const ignore = yield select(selectIgnoreActions);
  // if (ignore) return;

  switch (action.payload.type) {
    case EntityTypes.SCENE:
      let sceneAsset: IAsset = yield select(state => selectSceneAsset(state, action.payload.id));

      if (sceneAsset && sceneAsset.type === AssetType.VIDEO) yield loadSceneVideo(sceneAsset);

      break;
    case EntityTypes.ACTION: {
      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      const action: IActionBasic = yield select(state => selectAction(state, undefined));
      const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
      const hasVideo = (scene && scene.type === SceneType.VIDEO) || action?.voiceSceneId;

      if (action?.voiceSceneId) {
        const voiceSceneAsset = yield select(state => selectSceneAsset(state, action.voiceSceneId));
        if (voiceSceneAsset && voiceSceneAsset.type === AssetType.VIDEO) {
          yield loadSceneVideo(voiceSceneAsset);
        }
      } else if (!hasVideo && action && (!action.scene || action.scene.id !== sceneAsset?.entityId))
        yield removeVideo('action has no video');
      else if (hasVideo && action?.scene && scene.id === action.sceneId) {
        if (action.behaviour !== ActionBehaviour.PAUSE || action.voiceSceneId) yield startVideo(false);
        else yield put(yield call(updatePlayerState, { paused: true }));
      } else {
        //This is a general pause I had to put in for admin.
        const paused = yield select((state: RootState) => state.core.player.state.paused);
        if (!paused) yield put(yield call(updatePlayerState, { paused: true, culprit: 'video.updateStateSaga' }));
      }
    }
  }
}

function* prevSaga() {
  const { feed, index }: IActionFeed = yield select(selectActionFeed);
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  if (index === 0 && scene?.videoId) yield startVideo();
}

function* watchAssetLoaded() {
  yield takeEvery(loadAsset.fulfilled, assetLoaded);
}

function* watchPlayerState() {
  yield takeEvery(updatePlayerState.type, updatePlayerStateSaga);
}

function* watchUpdateState() {
  yield takeEvery(updateState.type, updateStateSaga);
}

function* watchPrev() {
  yield takeEvery(prev, prevSaga);
}

export function* videoSaga() {
  yield all([fork(watchUpdateState), fork(watchPlayerState), fork(watchAssetLoaded), fork(watchPrev)]);
}
